/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import React, { cloneElement, isValidElement, useState, MouseEvent, SyntheticEvent, ReactNode } from 'react'
import { isFunction } from 'lodash'
import { Button, ButtonProps, Divider, Menu, MenuItem, MenuProps } from '@mui/material'
import { MenuItem as MenuItemType, Divider as DividerType } from '../../types/Component.types'

type Variant = 'text' | 'contained' | 'outlined' | undefined

type Props = {
  buttonRenderer?: ReactNode | (() => ReactNode)
  button?: {
    text: string
    variant?: Variant
    color?: ButtonProps['color']
    classes?: any
  }
  id: string
  menuItems: (MenuItemType | DividerType)[]
  disabled?: boolean
} & Omit<MenuProps, 'open'>

export default function ButtonMenu(props: Props): JSX.Element {
  const {
    buttonRenderer,
    button,
    id,
    menuItems,
    disabled,
    anchorOrigin = { vertical: 'bottom', horizontal: 'left' } as MenuProps['anchorOrigin'],
    transformOrigin = { vertical: 'top', horizontal: 'left' } as MenuProps['transformOrigin'],
    ...restOfProps
  } = props
  const [anchorEl, setAnchorEl] = useState<any>(null)
  const open = !!anchorEl

  function onHandleOpen(evt: SyntheticEvent<HTMLAnchorElement | HTMLButtonElement>): void {
    if (!disabled) {
      evt.preventDefault()
      evt.stopPropagation()
      setAnchorEl(evt.currentTarget)
    }
  }

  function renderDefaultButton(extraProps: Partial<any> & React.Attributes): ReactNode {
    if (button) {
      const { text = '', variant = 'contained', color = 'primary', classes } = button
      const defaultButton = (
        <Button classes={classes} color={color} variant={variant} onClick={onHandleOpen}>
          {text}
        </Button>
      )
      return cloneElement(defaultButton, extraProps)
    }

    return null
  }

  function renderButton(): ReactNode {
    const extraProps = {
      onClick: onHandleOpen,
      'aria-controls': id,
      'aria-haspopup': 'true',
      'data-isopen': open,
      disabled,
    }
    if (buttonRenderer) {
      if (isValidElement(buttonRenderer)) {
        return cloneElement(buttonRenderer, extraProps)
      }
      if (isFunction(buttonRenderer)) {
        const Element = buttonRenderer()
        if (isValidElement(Element)) {
          return cloneElement(Element, extraProps)
        }
      } else return null
    }

    return renderDefaultButton(extraProps)
  }

  function renderMenuItems(): ReactNode {
    if (!menuItems || !Array.isArray(menuItems)) {
      return []
    }
    return menuItems.map((item, index) => {
      if (item.type === 'MenuItem') {
        return (
          <MenuItem
            css={css`
              .MuiDivider-wrapper {
                font-size: 0.875rem;
                color: hsl(0, 0%, 54%);
              }
            `}
            key={`${id}-menu-item-${index}`}
            onClick={(evt: MouseEvent<HTMLLIElement>) => {
              item.onClick(evt)
              setAnchorEl(null)
            }}
            disabled={item.disabled}
          >
            {item.content}
          </MenuItem>
        )
      }
      if (item.type === 'Divider') {
        return (
          <Divider key={`${id}-divider-${index}`} textAlign={item.textAlign}>
            {item.text}
          </Divider>
        )
      }
    })
  }

  function renderMenu(): ReactNode {
    return (
      <Menu
        {...restOfProps}
        id={id}
        anchorEl={anchorEl}
        elevation={4}
        keepMounted
        open={open}
        onClose={(): void => setAnchorEl(null)}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        {renderMenuItems()}
      </Menu>
    )
  }

  return (
    <div>
      {renderButton()}
      {!disabled && renderMenu()}
    </div>
  )
}
